import {
  Component,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  ViewChildren,
  ElementRef,
  QueryList,
  AfterViewChecked,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';

import { AiChatMessage } from '../../../models/skill-learning.model';

export interface AiChatDraftInsert {
  id: number;
  text: string;
}

interface TextInsertRange {
  start: number;
  end: number;
}

interface TextInsertResult {
  value: string;
  caret: number;
}

@Component({
  selector: 'app-ai-chat',
  templateUrl: './ai-chat.component.html',
  styleUrls: ['./ai-chat.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AiChatComponent implements AfterViewChecked, OnChanges {

  @Input() messages: AiChatMessage[] = [];
  @Input() isLoading: boolean = false;
  @Input() isDisabled: boolean = false;
  @Input() draftInsert: AiChatDraftInsert | null = null;
  @Input() maxMessageLength: number = 0;
  @Input() errorMessageKey: string = '';
  @Input() errorMessageParams: { [key: string]: number } = {};

  @Output() messageSent = new EventEmitter<string>();
  @Output() chatClosed = new EventEmitter<void>();

  @ViewChild('messagesContainer') private messagesContainer: ElementRef;
  @ViewChild('messageInput') private messageInput: ElementRef;
  @ViewChildren('messageItem') private messageItems: QueryList<ElementRef>;

  messageText: string = '';

  private shouldScrollToBottom: boolean = true;
  private assistantScrollIndex: number | null = null;
  private lastDraftInsertId: number = 0;

  trackByMessageIndex(index: number): number {
    return index;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.messages) {
      this.handleMessagesChange(changes.messages.previousValue, changes.messages.currentValue);
    }
    if (changes.draftInsert) {
      this.handleDraftInsertChange(changes.draftInsert.currentValue);
    }
  }

  ngAfterViewChecked(): void {
    if (this.assistantScrollIndex !== null) {
      this.scrollToMessageTop(this.assistantScrollIndex);
      this.assistantScrollIndex = null;
      return;
    }
    if (this.shouldScrollToBottom) {
      this.scrollToBottom();
      this.shouldScrollToBottom = false;
    }
  }

  onSendMessage(): void {
    const trimmed = this.messageText.trim();
    if (!trimmed || !this.canSendMessage) {
      return;
    }
    this.messageSent.emit(trimmed);
    this.messageText = '';
    this.shouldScrollToBottom = true;
  }

  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.onSendMessage();
    }
  }

  onClose(): void {
    this.chatClosed.emit();
  }

  get canSendMessage(): boolean {
    return !this.isDisabled && !this.isLoading && !this.isMessageTooLong && !!this.messageText.trim();
  }

  get hasMaxMessageLength(): boolean {
    return this.maxMessageLength > 0;
  }

  get isMessageTooLong(): boolean {
    return this.hasMaxMessageLength && this.messageLength > this.maxMessageLength;
  }

  get messageLength(): number {
    return (this.messageText || '').length;
  }

  get hasExternalError(): boolean {
    return !this.isMessageTooLong && !!this.errorMessageKey;
  }

  private handleMessagesChange(
    previous: AiChatMessage[] | null,
    current: AiChatMessage[] | null,
  ): void {
    if (!current || current.length === 0) {
      return;
    }
    if (this.shouldScrollToAssistant(previous, current)) {
      this.assistantScrollIndex = current.length - 1;
      return;
    }
    this.shouldScrollToBottom = this.shouldScrollToBottomAfterMessages(previous, current);
  }

  private shouldScrollToAssistant(previous: AiChatMessage[] | null, current: AiChatMessage[]): boolean {
    if (!previous || previous.length === 0) {
      return false;
    }
    if (current.length <= previous.length) {
      return false;
    }
    return current[current.length - 1].role === 'assistant';
  }

  private shouldScrollToBottomAfterMessages(previous: AiChatMessage[] | null, current: AiChatMessage[]): boolean {
    if (!previous) {
      return true;
    }
    return current.length > previous.length;
  }

  private handleDraftInsertChange(value: AiChatDraftInsert | null): void {
    if (!value || this.isDisabled || value.id === this.lastDraftInsertId) {
      return;
    }
    this.lastDraftInsertId = value.id;
    this.insertDraftText(value.text);
  }

  private insertDraftText(text: string): void {
    const element = this.getMessageInputElement();
    if (!element) {
      this.messageText = this.appendTextToEnd(this.messageText, text);
      return;
    }
    const result = this.buildInsertedMessage(element, text);
    this.messageText = result.value;
    this.scheduleCaretRestore(result.caret);
  }

  private buildInsertedMessage(element: HTMLTextAreaElement, text: string): TextInsertResult {
    const range = this.resolveInputRange(element);
    const insertText = this.buildInsertText(element.value, text, range);
    return {
      value: this.insertTextAtRange(element.value, insertText, range),
      caret: range.start + insertText.length,
    };
  }

  private resolveInputRange(element: HTMLTextAreaElement): TextInsertRange {
    return {
      start: element.selectionStart || 0,
      end: element.selectionEnd || element.selectionStart || 0,
    };
  }

  private buildInsertText(value: string, text: string, range: TextInsertRange): string {
    const prefix = value.slice(0, range.start);
    const suffix = value.slice(range.end);
    return this.resolveLeadingSpace(prefix) + text.trim() + this.resolveTrailingSpace(suffix);
  }

  private insertTextAtRange(value: string, insertText: string, range: TextInsertRange): string {
    return value.slice(0, range.start) + insertText + value.slice(range.end);
  }

  private appendTextToEnd(value: string, text: string): string {
    const safeValue = value || '';
    const range: TextInsertRange = { start: safeValue.length, end: safeValue.length };
    return safeValue + this.buildInsertText(safeValue, text, range);
  }

  private resolveLeadingSpace(prefix: string): string {
    if (!prefix || this.endsWithWhitespace(prefix)) {
      return '';
    }
    return ' ';
  }

  private resolveTrailingSpace(suffix: string): string {
    if (suffix && this.startsWithWhitespace(suffix)) {
      return '';
    }
    return ' ';
  }

  private startsWithWhitespace(value: string): boolean {
    return /^\s/.test(value);
  }

  private endsWithWhitespace(value: string): boolean {
    return /\s$/.test(value);
  }

  private scheduleCaretRestore(position: number): void {
    setTimeout(() => this.restoreInputCaret(position));
  }

  private restoreInputCaret(position: number): void {
    const element = this.getMessageInputElement();
    if (!element) {
      return;
    }
    element.focus();
    element.setSelectionRange(position, position);
  }

  private getMessageInputElement(): HTMLTextAreaElement | null {
    if (!this.messageInput) {
      return null;
    }
    return this.messageInput.nativeElement as HTMLTextAreaElement;
  }

  private scrollToMessageTop(index: number): void {
    if (!this.messagesContainer || !this.messageItems) {
      return;
    }
    const items = this.messageItems.toArray();
    if (!items[index]) {
      return;
    }
    this.setContainerScrollTop(items[index].nativeElement as HTMLElement);
  }

  private setContainerScrollTop(item: HTMLElement): void {
    const container = this.messagesContainer.nativeElement as HTMLElement;
    container.scrollTop = item.offsetTop - container.offsetTop;
  }

  private scrollToBottom(): void {
    if (this.messagesContainer) {
      const el = this.messagesContainer.nativeElement as HTMLElement;
      el.scrollTop = el.scrollHeight;
    }
  }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/common/skill-learning/components/user/ai-chat/ai-chat.component.ts